import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  BookOpen,
  Sparkles,
  Eye,
  X,
  Loader2,
} from 'lucide-react';
import { api } from '../../../../shared/api/api.js';
import { LazyBookCover } from '../../../../shared/components/LazyBookCover.js';
import { resolveCoverUrl } from '../../../../shared/utils/imageCompressor.js';

interface BookSearchAutocompleteProps {
  searchKeyword: string;
  onSearchKeywordChange: (val: string) => void;
  onSelectBookDetail: (bookId: string, fallbackTitle: string) => void;
  searchTarget?: 'all' | 'title' | 'author';
  onSearchTargetChange?: (target: 'all' | 'title' | 'author') => void;
}

export const BookSearchAutocomplete: React.FC<BookSearchAutocompleteProps> = ({
  searchKeyword,
  onSearchKeywordChange,
  onSelectBookDetail,
  searchTarget = 'all',
  onSearchTargetChange,
}) => {
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Keyboard shortcut listener to focus search when '/' is pressed
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const activeTag = document.activeElement?.tagName.toLowerCase();
      const isInputActive = activeTag === 'input' || activeTag === 'textarea' || (document.activeElement as HTMLElement)?.isContentEditable;
      if (e.key === '/' && !isInputActive) {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Real-time Autocomplete Query powered by FTS5
  const { data: autocompleteData, isLoading: isAutocompleteLoading } = useQuery({
    queryKey: ['books-autocomplete-fts5', searchKeyword],
    queryFn: async () => {
      const res = await api.getSearchSuggestions(searchKeyword.trim());
      return res.suggestions || [];
    },
    enabled: searchKeyword.trim().length >= 1,
    staleTime: 1000 * 20,
  });

  const suggestions = autocompleteData || [];

  // Close autocomplete on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsAutocompleteOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation for autocomplete suggestions
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isAutocompleteOpen || suggestions.length === 0) {
      if (e.key === 'ArrowDown' && suggestions.length > 0) {
        setIsAutocompleteOpen(true);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter') {
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        e.preventDefault();
        selectSuggestion(suggestions[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsAutocompleteOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const selectSuggestion = (item: any) => {
    onSearchKeywordChange(item.title);
    setIsAutocompleteOpen(false);
    setHighlightedIndex(-1);
  };

  const handleSelectAndOpenDetail = (e: React.MouseEvent, item: any) => {
    e.stopPropagation();
    setIsAutocompleteOpen(false);
    onSelectBookDetail(item.id, item.title);
  };

  // Helper to highlight matching text in title / author / UIBN
  const highlightMatch = (text: string | undefined, query: string) => {
    if (!text) return '';
    if (!query.trim()) return text;
    const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return (
      <span>
        {parts.map((part, i) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <mark key={`highlight-${i}`} className="bg-amber-200/90 text-amber-950 font-bold px-0.5 rounded-xs">
              {part}
            </mark>
          ) : (
            <span key={`text-${i}`}>{part}</span>
          )
        )}
      </span>
    );
  };

  return (
    <div ref={searchContainerRef} className="relative flex-1">
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
        <input
          ref={searchInputRef}
          id="input-search-books"
          type="text"
          placeholder={
            searchTarget === 'title'
              ? 'Cari cepat berdasarkan judul buku...'
              : searchTarget === 'author'
              ? 'Cari cepat berdasarkan nama penulis...'
              : 'Cari cepat berdasarkan judul buku atau nama penulis...'
          }
          value={searchKeyword}
          onChange={(e) => {
            onSearchKeywordChange(e.target.value);
            setIsAutocompleteOpen(true);
            setHighlightedIndex(-1);
          }}
          onFocus={() => {
            if (searchKeyword.trim().length >= 1) {
              setIsAutocompleteOpen(true);
            }
          }}
          onKeyDown={handleSearchKeyDown}
          aria-autocomplete="list"
          aria-expanded={isAutocompleteOpen && suggestions.length > 0}
          className="w-full pl-9 pr-16 py-2 text-xs sm:text-sm border border-slate-200 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition bg-white"
        />

        {/* Loading or Clear Button or Shortcut Badge inside search input */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 z-10">
          {isAutocompleteLoading && searchKeyword.trim().length >= 1 ? (
            <Loader2 className="w-3.5 h-3.5 text-indigo-500 animate-spin" />
          ) : searchKeyword ? (
            <button
              type="button"
              onClick={() => {
                onSearchKeywordChange('');
                setIsAutocompleteOpen(false);
                searchInputRef.current?.focus();
              }}
              className="p-0.5 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition cursor-pointer"
              title="Bersihkan kata kunci (Esc)"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          ) : (
            <kbd
              className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] font-mono font-medium text-slate-400 bg-slate-100 border border-slate-200 rounded"
              title="Tekan tombol / untuk langsung mencari"
            >
              /
            </kbd>
          )}
        </div>
      </div>

      {/* Target pills for quick filtering by title or author */}
      {onSearchTargetChange && (
        <div className="flex items-center gap-1.5 mt-2 text-xs flex-wrap">
          <span className="text-[11px] font-medium text-slate-400 mr-0.5">Cari di:</span>
          <button
            type="button"
            id="btn-search-target-all"
            onClick={() => onSearchTargetChange('all')}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition cursor-pointer min-h-[44px] flex items-center justify-center shrink-0 ${
              searchTarget === 'all'
                ? 'bg-indigo-600 text-white shadow-2xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80'
            }`}
          >
            Semua
          </button>
          <button
            type="button"
            id="btn-search-target-title"
            onClick={() => onSearchTargetChange('title')}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition cursor-pointer min-h-[44px] flex items-center justify-center shrink-0 ${
              searchTarget === 'title'
                ? 'bg-indigo-600 text-white shadow-2xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80'
            }`}
          >
            Judul Buku
          </button>
          <button
            type="button"
            id="btn-search-target-author"
            onClick={() => onSearchTargetChange('author')}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition cursor-pointer min-h-[44px] flex items-center justify-center shrink-0 ${
              searchTarget === 'author'
                ? 'bg-indigo-600 text-white shadow-2xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80'
            }`}
          >
            Nama Penulis
          </button>
        </div>
      )}

      {/* Real-time Autocomplete Dropdown Popover */}
      {isAutocompleteOpen && searchKeyword.trim().length >= 1 && (
        <div className="absolute left-0 right-0 top-full mt-1.5 z-40 bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden divide-y divide-slate-100 max-h-80 overflow-y-auto">
          <div className="px-3.5 py-2 bg-slate-50/90 text-[11px] font-semibold text-slate-600 flex items-center justify-between border-b border-slate-100">
            <span className="flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-indigo-500" />
              Saran Judul Buku Real-time
            </span>
            <span className="text-[10px] text-slate-400">{suggestions.length} saran</span>
          </div>

          {isAutocompleteLoading && suggestions.length === 0 ? (
            <div className="p-4 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />
              <span>Mencari rekomendasi judul buku...</span>
            </div>
          ) : suggestions.length === 0 ? (
            <div className="p-4 text-center text-xs text-slate-500 space-y-1">
              <p className="font-medium text-slate-700">Tidak ada saran judul yang cocok</p>
              <p className="text-[11px] text-slate-400">
                Coba ketik kata kunci judul, nama penulis, atau kode UIBN lainnya.
              </p>
            </div>
          ) : (
            <ul className="py-1" role="listbox">
              {suggestions.map((item, index) => {
                const isHighlighted = highlightedIndex === index;
                return (
                  <li
                    key={`${item.id}-${index}`}
                    role="option"
                    aria-selected={isHighlighted}
                    onClick={() => selectSuggestion(item)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`px-3.5 py-2.5 cursor-pointer transition flex items-center justify-between gap-3 ${
                      isHighlighted ? 'bg-indigo-50/90 text-indigo-950' : 'hover:bg-slate-50 text-slate-800'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {/* Thumbnail / Icon */}
                      <LazyBookCover
                        coverUrl={resolveCoverUrl(item.coverUrl)}
                        title={item.title}
                        className="w-9 h-12 rounded-lg shrink-0 shadow-2xs"
                        targetWidth={160}
                        quality={80}
                        priority={true}
                      />

                      {/* Title & Info */}
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-slate-900 truncate">
                          {highlightMatch(item.title, searchKeyword)}
                        </p>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500 mt-0.5">
                          <span>Penulis: {highlightMatch(item.authorName, searchKeyword)}</span>
                          <span>•</span>
                          <span className="font-mono text-[10px] text-slate-600 font-semibold bg-slate-100 px-1.5 py-0.2 rounded">
                            {highlightMatch(item.uibn, searchKeyword)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Category & Quick View */}
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                        {item.category || 'Umum'}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => handleSelectAndOpenDetail(e, item)}
                        className="p-1 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-white transition border border-transparent hover:border-indigo-200 cursor-pointer"
                        title="Buka Detail Buku"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
